"use client";

import { type ReactNode, useState } from "react";
import { Link } from "react-router-dom";
import { css, cx } from "styled-system/css";
import { MonitorSmartphone, X } from "lucide-react";
import { usePwaInstall } from "@/hooks/usePwaInstall";

const LANDING_SANS = '"IBM Plex Sans", ui-sans-serif, system-ui, sans-serif';
const LANDING_DISPLAY = '"Spectral", ui-serif, Georgia, serif';
const EASE = "cubic-bezier(0.23, 1, 0.32, 1)";

function AppleLogo() {
  return (
    <svg viewBox="0 0 384 512" width="17" height="17" fill="currentColor" aria-hidden="true">
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
    </svg>
  );
}

function AndroidLogo() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <g fill="#3DDC84">
        <rect x="6.6" y="1.6" width="1.6" height="4" rx="0.8" transform="rotate(-28 7.4 3.6)" />
        <rect x="15.8" y="1.6" width="1.6" height="4" rx="0.8" transform="rotate(28 16.6 3.6)" />
        <path d="M5 10.3a7 7 0 0 1 14 0z" />
        <rect x="4.3" y="10.3" width="15.4" height="7.6" rx="2" />
        <rect x="1.6" y="10.6" width="2.2" height="7" rx="1.1" />
        <rect x="20.2" y="10.6" width="2.2" height="7" rx="1.1" />
        <rect x="7.3" y="18.3" width="2.2" height="3.6" rx="1.1" />
        <rect x="14.5" y="18.3" width="2.2" height="3.6" rx="1.1" />
      </g>
      <circle cx="9" cy="9" r="0.9" fill="white" />
      <circle cx="15" cy="9" r="0.9" fill="white" />
    </svg>
  );
}

const baseButton = css({
  display: "flex", alignItems: "center", justifyContent: "center", gap: "2",
  flex: "1", minW: 0, px: "4", py: "3", rounded: "xl", fontSize: "13px", fontWeight: "bold",
  textDecoration: "none", cursor: "pointer", textAlign: "center", lineHeight: "1.25",
  transition: `transform 0.16s ${EASE}, box-shadow 0.16s ease, border-color 0.16s ease`,
  _active: { transform: "scale(0.97)" },
});

const activeButton = css({
  color: "white", bg: "#111827", border: "1.5px solid #111827",
  boxShadow: "0 8px 20px rgba(15,23,42,0.28)",
  _hover: { transform: "translateY(-1px)" },
});

const mutedButton = css({
  color: "#334155", bg: "white", borderWidth: "1.5px", borderColor: "#e2e8f0",
  _hover: { borderColor: "#cbd5e1", bg: "#f8fafc" },
});

export function InstallAppBanner() {
  const { platform, isInstalled, canPrompt, promptInstall } = usePwaInstall();
  const [showFallback, setShowFallback] = useState(false);
  const [busy, setBusy] = useState(false);

  if (isInstalled) return null;

  async function handleAndroidClick() {
    if (!canPrompt) {
      setShowFallback(true);
      return;
    }
    setBusy(true);
    try {
      await promptInstall();
    } finally {
      setBusy(false);
    }
  }

  const appleActive = platform === "ios";
  const androidActive = platform === "android";

  return (
    <>
      <section
        style={{ fontFamily: LANDING_SANS }}
        className={css({ bg: "#eef2ff", borderBottomWidth: "1px", borderColor: "#e0e7ff" })}
      >
        <div
          className={css({
            maxW: "1120px", mx: "auto", px: { base: "5", md: "8" }, py: { base: "5", md: "6" },
            display: "flex", flexDir: { base: "column", md: "row" },
            alignItems: { base: "stretch", md: "center" }, justifyContent: "space-between", gap: "4",
          })}
        >
          <div className={css({ display: "flex", alignItems: "center", gap: "4" })}>
            <div
              className={css({
                w: "12", h: "12", rounded: "xl", bg: "white", display: "grid", placeItems: "center",
                flexShrink: 0, borderWidth: "1px", borderColor: "#c7d2fe", color: "#4338CA",
              })}
            >
              <MonitorSmartphone size={24} strokeWidth={1.75} aria-hidden />
            </div>
            <div>
              <p className={css({ fontWeight: "bold", color: "#1e1b4b", fontSize: "md", m: 0 })}>
                Instalá SFTurnos en tu celular
              </p>
              <p className={css({ mt: "0.5", fontSize: "sm", color: "#4c4f6b", m: 0 })}>
                Accedé más rápido, sin buscarlo en el navegador. Es gratis y no ocupa espacio.
              </p>
            </div>
          </div>

          <div className={css({ display: "flex", gap: "2.5", flexShrink: 0, w: { base: "100%", md: "auto" } })}>
            <Link
              to="/instalar-ios"
              className={cx(baseButton, appleActive ? activeButton : mutedButton)}
            >
              <AppleLogo />
              Instalá en iPhone / iPad
            </Link>
            <button
              type="button"
              onClick={() => void handleAndroidClick()}
              disabled={busy}
              className={cx(
                baseButton,
                css({ _disabled: { opacity: 0.6, cursor: "not-allowed" } }),
                androidActive ? activeButton : mutedButton,
              )}
            >
              <AndroidLogo />
              {busy ? "Abriendo..." : "Instalá en Android"}
            </button>
          </div>
        </div>
      </section>

      {showFallback && <AndroidFallbackModal onClose={() => setShowFallback(false)} />}
    </>
  );
}

function ModalShell({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  return (
    <div
      role="dialog"
      aria-modal
      onClick={(e) => e.currentTarget === e.target && onClose()}
      className={css({
        position: "fixed", inset: 0, zIndex: 100, bg: "rgba(15,23,42,0.55)",
        display: "flex", alignItems: { base: "flex-end", md: "center" }, justifyContent: "center",
        p: { base: "0", md: "4" },
      })}
      style={{ fontFamily: LANDING_SANS }}
    >
      <div
        className={css({
          bg: "white", width: "100%", maxW: { base: "100%", md: "420px" },
          roundedTop: "2xl", roundedBottom: { base: "none", md: "2xl" },
          p: "6", pt: "7", position: "relative", maxH: "90dvh", overflowY: "auto",
        })}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar"
          className={css({
            position: "absolute", top: "4", right: "4", w: "8", h: "8", rounded: "full",
            bg: "#f1f5f9", display: "grid", placeItems: "center", border: "none", cursor: "pointer",
            color: "#64748b", _hover: { bg: "#e2e8f0" },
          })}
        >
          <X size={16} aria-hidden />
        </button>
        {children}
      </div>
    </div>
  );
}

function AndroidFallbackModal({ onClose }: { onClose: () => void }) {
  return (
    <ModalShell onClose={onClose}>
      <div
        className={css({ w: "12", h: "12", rounded: "xl", bg: "#eef2ff", color: "#4338CA", display: "grid", placeItems: "center", mb: "4" })}
      >
        <MonitorSmartphone size={24} strokeWidth={1.75} aria-hidden />
      </div>
      <h3 style={{ fontFamily: LANDING_DISPLAY, fontWeight: 600, fontSize: 22, margin: 0, color: "#0f172a" }}>
        No pudimos abrir la instalación automática
      </h3>
      <p className={css({ mt: "3", fontSize: "sm", color: "#51607a", lineHeight: "1.65" })}>
        Abrí este sitio desde <b>Chrome</b> en tu celular Android y volvé a tocar "Instalá en
        Android". Si ya estás en Chrome, buscá <b>"Instalar app"</b> o <b>"Agregar a pantalla de
        inicio"</b> en el menú ⋮ de la barra superior.
      </p>
      <button
        type="button"
        onClick={onClose}
        className={css({
          mt: "6", w: "100%", py: "3", rounded: "xl", fontWeight: "bold", fontSize: "sm",
          color: "white", bg: "#111827", border: "none", cursor: "pointer",
          transition: `transform 0.16s ${EASE}`, _active: { transform: "scale(0.97)" },
        })}
      >
        Entendido
      </button>
    </ModalShell>
  );
}
